import React, { FunctionComponent } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import { Breadcrumb } from 'src/components/breadcrumb';
import { CartInformation } from 'src/components/cart';
import { Spinner } from 'src/components/spinner';
import { usePurchaseEligibilityQuery } from 'src/hooks/use-purchase-eligibility-query';
import { useSelectSubmitShoppingCartState } from 'src/hooks/use-submit-shopping-cart-mutation';
import { useProductsQuery } from 'src/hooks/use-products-query';
import { ProductSelectPage } from 'src/pages/product-select/product-select-page';
import { PurchasePage } from 'src/pages/purchase/purchase-page';
import { SuccessPage } from 'src/pages/success/success-page';
import { SummaryPage } from 'src/pages/summary/summary-page';
import { NavigationService } from 'src/services/navigation-service';

import { UpgradePage } from './upgrade/upgrade-page';

export const Store: FunctionComponent = () => {
  const path = useLocation().pathname;
  const { isSuccess } = useSelectSubmitShoppingCartState();
  const isOrderCompleted = isSuccess && path !== NavigationService.SUMMARY;
  const { productsResponse, isLoading: loadingProducts } = useProductsQuery();
  const { purchaseEligibility, isLoading: loadingPurchaseEligibility } = usePurchaseEligibilityQuery();
  const loading = loadingPurchaseEligibility || loadingProducts;

  if (loading) {
    return <Spinner />;
  }

  if (!productsResponse) {
    return <Navigate to={NavigationService.ERROR} replace={true} />;
  }

  // eslint-disable-next-line default-case
  switch (productsResponse.status) {
    case 'NO_BROADBAND_OFFER':
      return <Navigate to={NavigationService.ERROR_NO_BROADBAND} replace={true} />;
    case 'MULTIPLE_TV_OFFERS':
      return <Navigate to={NavigationService.ERROR_MULTIPLE_TV_OFFERS} replace={true} />;
    case 'TV_OFFER_NOT_AVAILABLE':
      return <Navigate to={NavigationService.ERROR_NO_TV_OFFER} replace={true} />;
    case 'CUSTOMER_ON_WRONG_TV_PLATFORM':
      return <Navigate to={NavigationService.ERROR_WRONG_TV_PLATFORM} replace={true} />;
  }

  if (!purchaseEligibility) {
    return <Navigate to={NavigationService.ERROR} replace={true} />;
  }

  // eslint-disable-next-line default-case
  switch (purchaseEligibility.status) {
    case 'PURCHASE_RESTRICTION':
      return <Navigate to={NavigationService.ERROR_RESTRICTED_PURCHASE} replace={true} />;
    case 'OPEN_ORDER':
      return <Navigate to={NavigationService.ERROR_HAS_INFLIGHT_ORDER} replace={true} />;
    case 'USER_ROLE':
      return <Navigate to={NavigationService.WRONG_ROLE} replace={true} />;
  }

  return (
    <>
      <Breadcrumb />
      {!isOrderCompleted && <CartInformation />}
      <Routes>
        <Route path={NavigationService.PRODUCT_SELECT} element={<ProductSelectPage />} />
        <Route path={NavigationService.PRODUCT_PURCHASE} element={<PurchasePage />} />
        <Route path={NavigationService.PRODUCT_UPGRADE} element={<UpgradePage />} />
        <Route path={NavigationService.SUMMARY} element={<SummaryPage />} />
        <Route path={NavigationService.SUCCESS} element={<SuccessPage />} />
        <Route path="*" element={<Navigate to={NavigationService.CHOOSE_ACCOUNT} replace={true} />} />
      </Routes>
    </>
  );
};
